'use client';

import type { FC } from 'react';
import { useEffect } from 'react';
import type { IHeadlinePromoDataApi } from '@/api/cms/headlinePromo';
import { useNavigation } from '@/hooks/navigation';
import { useTimeoutRegistrationModal } from '@/hooks/timeoutRegistrationModal';
import { useGetCommonStore } from '@/store';
import { usePromotionsAvailability } from '../../hooks';
import { Faq } from './components/Faq';
import { Hero } from './components/Hero';
import { HowTo } from './components/HowTo';
import type { IHeadlinePromoPageConfig } from './helpers';
import { checkHeadlinePromoCountryMatch } from './helpers/checkHeadlinePromoCountryMatch';
import { ScHeadlinePromoPage } from './styled';

interface IHeadlinePromoPageProps {
  config: IHeadlinePromoPageConfig;
  data: IHeadlinePromoDataApi | null;
}

const HeadlinePromoTemplate: FC<IHeadlinePromoPageProps> = ({
  config,
  data,
}) => {
  const {
    promoKey,
    namespace,
    groupPrefix,
    useFullNameAsGroupId,
    cardHud,
    levelsConfig,
    levelsMobileBreakpoint,
    disableAnimations,
    dateFormat,
    hideHowToGamesCountLabel,
    hideHowToDescription,
    faqBgImage,
    progressBarVariant,
    ctaAlwaysDeposit,
    allowedCountries,
    disallowedCountries,
  } = config;

  useTimeoutRegistrationModal();

  const { isHeadlinePromoAvailable } = usePromotionsAvailability(promoKey);

  const { navigate } = useNavigation();
  const useCommonStore = useGetCommonStore();
  const user = useCommonStore.use.user();
  const countryCode = useCommonStore.use.countryCode();
  const isCountryMatch = checkHeadlinePromoCountryMatch(
    allowedCountries,
    disallowedCountries,
    user?.country || countryCode
  );

  useEffect(() => {
    if (!(isHeadlinePromoAvailable(promoKey) && isCountryMatch)) navigate('/');
  }, [isHeadlinePromoAvailable, isCountryMatch, promoKey, navigate]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'instant',
      });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <ScHeadlinePromoPage>
      <Hero
        cardHud={cardHud}
        ctaAlwaysDeposit={ctaAlwaysDeposit}
        dateFormat={dateFormat}
        disableAnimations={disableAnimations}
        groupPrefix={groupPrefix}
        levelsConfig={levelsConfig}
        levelsMobileBreakpoint={levelsMobileBreakpoint}
        namespace={namespace}
        progressBarVariant={progressBarVariant}
        promoKey={promoKey}
        useFullNameAsGroupId={useFullNameAsGroupId}
      />
      <HowTo
        hideDescription={hideHowToDescription}
        hideGamesCountLabel={hideHowToGamesCountLabel}
        namespace={namespace}
        sections={data ? data.howToParticipate : null}
      />
      <Faq
        bgImage={faqBgImage}
        namespace={namespace}
        sections={data ? data.faq : null}
      />
    </ScHeadlinePromoPage>
  );
};

export default HeadlinePromoTemplate;
